import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { IMAGES } from "@/data/site";
import { HERO } from "@/constants/testIds";

const easeOut = [0.22, 1, 0.36, 1];
const lines = ["Learn Technology.", "Build Skills.", "Stay Ahead."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: easeOut, delay: 0.15 + i * 0.14 },
  }),
};

export default function Hero() {
  const navigate = useNavigate();

  const scrollToTopics = () => {
    document.getElementById("topics")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid={HERO.section}
      className="relative overflow-hidden bg-gradient-to-br from-neutral-950 via-black to-ink-800 pt-28 pb-20 sm:pt-36 sm:pb-28"
    >
      <div className="ts-grid-bg pointer-events-none absolute inset-0 opacity-60" />
      {/* Parallax abstract image */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.4, ease: easeOut }}
        className="pointer-events-none absolute -right-24 -top-16 h-[520px] w-[520px] rounded-full"
        style={{
          backgroundImage: `url(${IMAGES.heroAbstract})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "sepia(1) saturate(2.2) hue-rotate(3deg) brightness(0.85) blur(2px)",
          maskImage: "radial-gradient(circle at center, black 40%, transparent 72%)",
          WebkitMaskImage: "radial-gradient(circle at center, black 40%, transparent 72%)",
        }}
      />
      <div className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-cyan-accent/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: easeOut }}
          className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-4 py-1.5 text-xs font-semibold text-brand"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Practical tech education for creators & businesses
        </motion.div>

        <h1 className="mt-7 max-w-4xl font-display text-5xl font-black leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[5.5rem]">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
              >
                {i === 2 ? (
                  <span className="bg-gradient-to-r from-brand to-cyan-accent bg-clip-text text-transparent">
                    {line}
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeOut, delay: 0.7 }}
          className="mt-7 max-w-2xl text-lg leading-relaxed text-neutral-400"
        >
          Tech Siksha shares practical content on AI tools, website development,
          mobile apps, productivity, PC tips, automation, and modern technology.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeOut, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid={HERO.explore}
            onClick={scrollToTopics}
            className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-black shadow-[0_4px_14px_0_rgb(212,175,55,0.39)] transition-transform duration-200 hover:-translate-y-0.5"
          >
            Explore
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            data-testid={HERO.contact}
            onClick={() => navigate("/contact")}
            className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-7 py-3.5 text-sm font-semibold text-neutral-100 transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 hover:border-brand hover:bg-white/[0.08]"
          >
            Contact
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
